import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  WritableSignal,
  signal,
  effect,
  untracked,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, ActivatedRoute, UrlTree } from '@angular/router';
import { Location } from '@angular/common';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LazyLoadEvent } from 'primeng/api';
import {
  ActiveFilterService,
  ColumnDefinitionService,
  MetricsGridConfig,
  BreadcrumbService,
  GridTableConfiguredExpandableComponent,
  FormatService,
  GridSortingService,
} from '../core';
import { DownloadsService } from '../downloads';
import { HeaderService } from '../header';
import { SalesRepBaseComponent } from './sales-rep-base.component';
import { SelectedSalesRepService } from './selected-sales-rep.service';
import { SalesRepCustomerService } from './sales-rep-customer.service';
import { PageOptionsNg, SortOptionsNg, SortOptions } from '../models';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { TranslateService } from '@ngx-translate/core';
import { FilterProfileUtilsService } from '@/app/filter/profiles/filter-profile-utils.service';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { createGridSerializer } from '@/app/modules/saved-views/saved-views-serializer';
import { savedViewDeserializer as deserializer } from '@/app/modules/saved-views/saved-views-deserializer';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

@Component({
  selector: 'rdo-product-type-customers',
  template: `
    <rdo-overlay [enabled]="loading"></rdo-overlay>
    <ng-container *featureFlag="'flagSavedViews'">
      <rdo-saved-views
        [gridName]="gridName"
        [pageName]="
          translateService.instant('main.core.common.counts.customers.plural')
        "
      />
    </ng-container>

    @if (data && totalCount > 0) {
      <rdo-grid-table-expanded-ng
        #grid
        [name]="gridName"
        [dataKey]="dataKey"
        [gridConfig]="gridConfig()"
        [rowExpansion]="gridConfig().rowExpansion"
        [pagedData]="data"
        [frozenRows]="frozenRows"
        [sorting]="sorting"
        [(paging)]="paging"
        (pageOnChange)="changePage($event)"
        (lazyLoadOnChange)="changeLazyLoad($event)"
        [totalCount]="totalCount"
        [frozenWidth]="frozenWidth"
        [showColumnSelector]="true"
        (downloadExcelClick)="exportExcel($event)"
        [alwaysShowPager]="false"
      >
      </rdo-grid-table-expanded-ng>
    }
  `,
  standalone: false,
})
export class SalesRepCustomersComponent
  extends SalesRepBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('grid')
  customersGrid: GridTableConfiguredExpandableComponent;
  loading = true;
  gridName = 'SALESREP_CUSTOMERS_GRID';
  data: any;
  salesRepId: string;
  gridConfig: WritableSignal<MetricsGridConfig> = signal(null);
  frozenRows: any;
  paging = new PageOptionsNg();
  sorting: SortOptionsNg;
  totalCount = 0;
  frozenWidth: string;
  dataKey = 'CustomerId';
  private lastAppliedViewName: WritableSignal<string> = signal(null);

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private columnService: ColumnDefinitionService,
    private filterService: ActiveFilterService,
    private headerService: HeaderService,
    private customerService: SalesRepCustomerService,
    breadcrumbService: BreadcrumbService,
    selectedSalesRepService: SelectedSalesRepService,
    private downloadsService: DownloadsService,
    private formatService: FormatService,
    private gridSortingService: GridSortingService,
    private filterProfileUtilsService: FilterProfileUtilsService,
    private savedViewsService: SavedViewsService,
    public translateService: TranslateService,
    protected featureFlagService: FeatureFlagService,
    private cdr: ChangeDetectorRef
  ) {
    super(selectedSalesRepService, breadcrumbService);

    // React to saved view changes - track the view NAME to prevent infinite loops
    effect(() => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (!areSavedViewsEnabled) {
        return;
      }
      const selectedView = this.savedViewsService.getSelectedView();
      const viewName = selectedView?.name ?? null;

      // Use untracked to read gridConfig
      const hasGridConfig = untracked(() => !!this.gridConfig());

      const lastViewName =
        this.savedViewsService.previouslySelectedViewsStack()[0];
      if (viewName === lastViewName) {
        return;
      }

      if (selectedView && hasGridConfig) {
        // Get current config in untracked context, but keep the signal update in tracked context
        const currentConfig = untracked(() => this.gridConfig());

        // Handle the async operation and update the signal when it completes
        this.savedViewsService
          .handleSavedViewChange(selectedView, currentConfig, deserializer)
          .then((response) => {
            response.config.groups.forEach((group) => {
              if (group.visible) {
              }
            });

            // Update the signal - this will trigger change detection
            this.gridConfig.set(response.config);
            this.cdr.detectChanges();

            // Signal that the full saved view application is complete
            this.savedViewsService.notifyViewApplied();
          });
      }
    });
  }

  ngOnInit() {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      this.savedViewsService.initGrid(this.gridName, this.getSerializer());
    }
    this.gridSortingService.setGridName(this.gridName);
    this.sorting = this.gridSortingService.getSortOptionsNg();

    const component = this; // eslint-disable-line @typescript-eslint/no-this-alias
    let salesRepId = component.salesRepId;

    component.subscriptions.push(
      component.route.parent.params.subscribe(async (params) => {
        const areSavedViewsEnabled =
          this.featureFlagService.isActive('flagSavedViews');
        salesRepId = params.salesRep;

        if (component.salesRepId !== salesRepId) {
          component.salesRepId = salesRepId;
          component.configureGrid();
          if (areSavedViewsEnabled) {
            await component.savedViewsService.load();
          }
          component.load();
        }
      })
    );

    // When the filters change reset the page.
    this.subscriptions.push(
      this.filterService.filterChange.subscribe(() => {
        // If saved views is currently applying a view or its filters, skip —
        // the saved view effect will handle load.
        if (this.savedViewsService.isApplying()) {
          return;
        }
        this.paging.page = 1;
        if (this.customersGrid) {
          this.customersGrid.resetPaginator();
        }
        // Don't call configureGrid() here - it will wipe out saved view settings
        // Only reload the data with new filter values
        this.load();
      })
    );

    component.subscriptions.push(
      component.selectedSalesRepService.salesRepChange.subscribe((s) => {
        component.salesRepId = s.SalesRepId.toString();
        salesRepId = s.SalesRepId.toString();
        this.paging.page = 1;
        if (component.customersGrid) {
          component.customersGrid.resetPaginator();
        }
        component.load();
      })
    );

    this.subscriptions.push(
      this.authenticationService.userInfoView.subscribe((userInfo) => {
        if (this.gridConfig()) {
          const config = this.gridConfig();
          config.enableExcelExport = userInfo.HasClientAccessToExportData;
          this.gridConfig.set(config);
        }
      })
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  load = () => {
    this.loading = true;
    const sorting = this.getSorting();
    this.customerService
      .getSalesRepCustomers(this.salesRepId, this.paging, sorting)
      .subscribe((result) => {
        this.loading = false;
        if (result.Items && result.Items.length > 0) {
          this.data = result.Items;
        } else {
          this.data = [];
        }
        this.totalCount = result.TotalCount;
      });
    this.updateBreadcrumbs([
      { title: 'main.core.common.counts.customers.plural', class: 'active' },
    ]);
  };

  changePage = (event: any) => {
    if (event.rows !== this.paging.pageSize) {
      this.paging.pageSize = event.rows;
    }

    this.paging.page = event.first < 1 ? 1 : event.first / event.rows + 1;
    this.load();
  };

  changeLazyLoad = (event: LazyLoadEvent) => {
    if (
      this.sorting.sortField === event.sortField &&
      this.sorting.sortOrder === event.sortOrder
    ) {
      return;
    }

    this.gridSortingService.setSortOption(
      event.sortField,
      event.sortOrder === -1
    );

    this.sorting.sortField =
      event.sortField || this.gridSortingService.defaultSortColumn;
    this.sorting.sortOrder = event.sortOrder || -1;

    this.load();
  };

  private handleTransactionCountClick = (record: any, parent: any): boolean => {
    this.updateBreadcrumbs([
      {
        title: parent.CustomerName,
        class: 'active',
      },
      {
        title: record.Description,
        class: 'active',
      },
    ]);

    this.router.navigateByUrl(this.getTransactionUrlTree(record, parent));
    return false;
  };

  private getTransactionUrlTree = (record: any, parent: any): UrlTree => {
    return this.router.createUrlTree(
      [
        'transactions',
        {
          customer: parent.CustomerId,
          productType: record.ProductType,
        },
      ],
      {
        relativeTo: this.route.parent,
      }
    );
  };

  private generateTransactionsLink = (record: any, parent: any): string => {
    const urlTree = this.getTransactionUrlTree(record, parent);
    return this.location.prepareExternalUrl(urlTree.toString());
  };

  exportExcel = (args: any) => {
    this.loading = true;
    const sorting = this.getSorting();

    let salesRep = '';
    this.selectedSalesRepService.SalesRep.subscribe(
      (result) => (salesRep = result.SalesRepName)
    );
    const translatedConfig = this.gridConfig().cloneAndTranslate((text) =>
      this.formatService.translateAndFormat(text, false)
    );
    this.subscriptions.push(
      this.customerService
        .getSalesRepCustomersDownload(
          salesRep,
          this.salesRepId,
          sorting,
          translatedConfig
        )
        .subscribe((blob) => {
          this.loading = false;
          this.downloadsService.saveExcelBlob(blob);
        })
    );
  };

  protected getSorting = (): SortOptions => {
    this.sorting.sortField = this.gridSortingService.getSortFieldOrDefault();
    this.sorting.sortOrder =
      this.gridSortingService.getSortDirectionOrDefault();
    return new SortOptions(
      this.sorting.sortField,
      !(this.sorting.sortOrder === 1)
    );
  };

  private configureGrid = () => {
    console.log('[configureGrid] CALLED - Stack trace:');
    console.trace();

    // Capture current visibility settings before recreating config, keyed by columnSelectorTitle
    const existingConfig = this.gridConfig();
    const existingVisibilityMap = new Map<string, boolean>();
    if (existingConfig?.groups) {
      existingConfig.groups.forEach((g) => {
        if (g.columnSelectorTitle) {
          existingVisibilityMap.set(g.columnSelectorTitle, g.visible);
          console.log(
            '[configureGrid] Capturing visibility:',
            g.columnSelectorTitle,
            '=',
            g.visible
          );
        }
      });
    }

    this.frozenWidth = '525px';

    // switch the count column's header based on Primary Product Type filter value
    const productTypeCountColumn = this.columnService.ProductTypeCount(
      this.columnService.ProductType()
    );
    productTypeCountColumn.title = this.filterProfileUtilsService
      .getProductTypesHeader()
      .ambiguousCount();

    const config = new MetricsGridConfig(
      [
        {
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.customers.singular',
          columns: [
            {
              title: 'main.core.common.counts.customers.singular',
              field: 'CustomerName',
              sortColumn: 'CustomerName',
              minWidth: 100,
              width: 100,
              maxWidth: 255,
              autoWidth: true,
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              childConfig: {
                field: '',
              },
              isString: true,
            },
            productTypeCountColumn,
            this.columnService.TransactionCount(
              this.columnService.TransactionCount(
                null,
                (v, r, p) => this.generateTransactionsLink(r, p),
                (v, r, p) => this.handleTransactionCountClick(r, p)
              )
            ),
          ],
        },
        ...this.columnService.StandardMetricSetColumnGroups(),
      ],
      (p) => this.loadChildren(p),
      true
    );

    // Restore visibility settings from existing config (if any) to preserve saved view state
    if (existingVisibilityMap.size > 0) {
      config.groups.forEach((group) => {
        if (
          group.columnSelectorTitle &&
          existingVisibilityMap.has(group.columnSelectorTitle)
        ) {
          const restoredValue = existingVisibilityMap.get(
            group.columnSelectorTitle
          );
          console.log(
            '[configureGrid] Restoring visibility:',
            group.columnSelectorTitle,
            '=',
            restoredValue
          );
          group.visible = restoredValue;
        } else if (group.columnSelectorTitle) {
          console.log(
            '[configureGrid] No saved visibility for:',
            group.columnSelectorTitle,
            'keeping default:',
            group.visible
          );
        }
      });
    }

    console.log('[configureGrid] Final config groups visibility:');
    config.groups.forEach((g) => {
      if (g.columnSelectorTitle) {
        console.log('  ', g.columnSelectorTitle, '=', g.visible);
      }
    });

    console.log('[configureGrid] Setting gridConfig signal with new config');
    console.log('[configureGrid] Config object reference:', config);
    this.gridConfig.set(config);
    this.cdr.detectChanges();
    console.log('[configureGrid] Signal set complete');
  };

  private loadChildren = (parent: any): Observable<Array<any>> => {
    const sorting = this.getSorting();

    return this.customerService
      .getSalesRepCustomersProductTypes(
        this.salesRepId,
        parent.CustomerId,
        sorting
      )
      .pipe(
        map((items) => {
          return items.map(this.convert);
        })
      );
  };

  private convert = (item: any): any => {
    const clone = (<any>Object).assign({}, item);
    clone.CustomerName = null;
    clone.ProductTypeCount = item.Description;
    return clone;
  };

  private getSerializer = createGridSerializer({
    gridName: this.gridName,
    gridConfig: this.gridConfig,
    gridSortingService: this.gridSortingService,
  });
}
